import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  AlertColor,
  Backdrop,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { useState, ReactNode, useEffect } from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  testAPIConnection,
  saveDataSource,
  getDataSources,
  deleteDataSource,
} from "../../services/purecloud";
import { getStorageItem } from "../../services/applicationStorage";

interface CustomHeader {
  key: string;
  value: string;
}

interface DatasourceState {
  name: string;
  type: string;
  authMethod: string;
  httpMethod: string;
  testUrl: string;
  apiKey: string;
  apiKeyHeader: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthTokenUrl: string;
  oauthScope: string;
  basicAuthUsername: string;
  basicAuthPassword: string;
  bearerToken: string;
  refreshToken: string;
  customHeaders: CustomHeader[];
  apiDataFormat: string;
  apiRecordPath: string;
  requestBody: string;
  soapAction: string;
  soapEnvelope: string;
}

const Datasource = () => {
  const [isFormDataLoading, setFormDataLoading] = useState<boolean>(false);
  const [getSnackDetails, setSnackDetails] = useState({
    open: false,
    message: (<></>) as ReactNode,
    severity: "success",
  });
  const [datasources, setDatasources] = useState<Array<any>>([]);
  const [testResponse, setTestResponse] = useState<any>(null);

  useEffect(() => {
    loadDatasources();
  }, []);
  const [newDatasource, setNewDatasource] = useState<DatasourceState>({
    name: "",
    type: "",
    authMethod: "",
    httpMethod: "",
    testUrl: "",
    apiKey: "",
    apiKeyHeader: "X-API-Key",
    oauthClientId: "",
    oauthClientSecret: "",
    oauthTokenUrl: "",
    oauthScope: "", // Keep this for non-Zoho CRM datasources
    basicAuthUsername: "",
    basicAuthPassword: "",
    bearerToken: "",
    refreshToken: "",
    customHeaders: [],
    apiDataFormat: "json",
    apiRecordPath: "",
    requestBody: "",
    soapAction: "",
    soapEnvelope: "",
  });

  const authMethods = [
    "API Key",
    "OAuth 2.0",
    "Basic Auth",
    "Bearer Token",
    "Custom Headers",
    "Zoho CRM",
  ];

  const httpMethods = [
    "GET",
    "POST",
    "PUT",
    "PATCH",
    "DELETE",
    "HEAD",
    "OPTIONS",
  ];

  const datasourceTypes = [
    // {
    //   name: "",
    //   type: "Salesforce",
    //   authMethod: "OAuth 2.0",
    //   httpMethod: "GET",
    //   testUrl: "",
    //   apiKey: "",
    //   apiKeyHeader: "X-API-Key",
    //   oauthClientId: "",
    //   oauthClientSecret: "",
    //   oauthTokenUrl: "",
    //   oauthScope: "",
    //   basicAuthUsername: "",
    //   basicAuthPassword: "",
    //   bearerToken: "",
    //   refreshToken: "",
    //   customHeaders: [],
    //   apiDataFormat: "json",
    //   apiRecordPath: "",
    //   requestBody: "",
    // },
    {
      name: "",
      type: "Microsoft Dynamics",
      authMethod: "OAuth 2.0",
      httpMethod: "GET",
      testUrl: "",
      apiKey: "",
      apiKeyHeader: "X-API-Key",
      oauthClientId: "",
      oauthClientSecret: "",
      oauthTokenUrl: "",
      oauthScope: "",
      basicAuthUsername: "",
      basicAuthPassword: "",
      bearerToken: "",
      refreshToken: "",
      customHeaders: [],
      apiDataFormat: "json",
      apiRecordPath: "value",
      requestBody: "",
    },
    // {
    //   name: "",
    //   type: "Zoho CRM",
    //   authMethod: "Zoho CRM",
    //   httpMethod: "GET",
    //   testUrl: "https://www.zohoapis.com/crm/v2/contacts",
    //   apiKey: "",
    //   apiKeyHeader: "X-API-Key",
    //   oauthClientId: "",
    //   oauthClientSecret: "",
    //   oauthTokenUrl: "https://accounts.zoho.com/oauth/v2/token",
    //   oauthScope: "",
    //   basicAuthUsername: "",
    //   basicAuthPassword: "",
    //   bearerToken: "",
    //   refreshToken: "",
    //   customHeaders: [],
    //   apiDataFormat: "json",
    //   apiRecordPath: "",
    //   requestBody: "",
    // },
    // {
    //   name: "",
    //   type: "HealthCX",
    //   authMethod: "OAuth 2.0",
    //   httpMethod: "POST",
    //   testUrl: "https://hcx-usw2-qa.genesysinnoapps.net/gcloud/api/beta/autodialer/contactlist",
    //   apiKey: "",
    //   apiKeyHeader: "X-API-Key",
    //   oauthClientId: "",
    //   oauthClientSecret: "",
    //   oauthTokenUrl: "https://login.mypurecloud.com/oauth/token",
    //   oauthScope: "",
    //   basicAuthUsername: "",
    //   basicAuthPassword: "",
    //   bearerToken: "",
    //   refreshToken: "",
    //   customHeaders: [],
    //   apiDataFormat: "json",
    //   apiRecordPath: "data.ContactList",
    //   requestBody: "{\"ExternalId\": \"\", \"AgentQueueId\": \"\", \"Count\": \"\"}",
    // },
    {
      name: "",
      type: "Genesys Cloud",
      authMethod: "OAuth 2.0",
      httpMethod: "POST",
      testUrl: "",
      apiKey: "",
      apiKeyHeader: "X-API-Key",
      oauthClientId: "",
      oauthClientSecret: "",
      oauthTokenUrl: "https://login.mypurecloud.com/oauth/token",
      oauthScope: "",
      basicAuthUsername: "",
      basicAuthPassword: "",
      bearerToken: "",
      refreshToken: "",
      customHeaders: [],
      apiDataFormat: "json",
      apiRecordPath: "",
      requestBody: "",
    },
    {
      name: "",
      type: "ServiceNow",
      authMethod: "OAuth 2.0",
      httpMethod: "GET",
      testUrl: "",
      apiKey: "",
      apiKeyHeader: "X-API-Key",
      oauthClientId: "",
      oauthClientSecret: "",
      oauthTokenUrl: "",
      oauthScope: "",
      basicAuthUsername: "",
      basicAuthPassword: "",
      bearerToken: "",
      refreshToken: "",
      customHeaders: [],
      apiDataFormat: "json",
      apiRecordPath: "result",
      requestBody: "",
    },
    {
      name: "",
      type: "SOAP Web Service",
      authMethod: "Basic Auth",
      httpMethod: "POST",
      testUrl: "",
      apiKey: "",
      apiKeyHeader: "X-API-Key",
      oauthClientId: "",
      oauthClientSecret: "",
      oauthTokenUrl: "",
      oauthScope: "",
      basicAuthUsername: "",
      basicAuthPassword: "",
      bearerToken: "",
      refreshToken: "",
      customHeaders: [],
      apiDataFormat: "xml",
      apiRecordPath: "",
      requestBody: "",
      soapAction: "",
      soapEnvelope: "",
    },
    {
      name: "",
      type: "Custom API",
      authMethod: "",
      httpMethod: "",
      testUrl: "",
      apiKey: "",
      apiKeyHeader: "X-API-Key",
      oauthClientId: "",
      oauthClientSecret: "",
      oauthTokenUrl: "",
      oauthScope: "",
      basicAuthUsername: "",
      basicAuthPassword: "",
      bearerToken: "",
      refreshToken: "",
      customHeaders: [],
      apiDataFormat: "json",
      apiRecordPath: "",
      requestBody: "",
      soapAction: "",
      soapEnvelope: "",
    },
  ];

  const loadDatasources = async () => {
    try {
      setFormDataLoading(true);
      const response = await getDataSources();
      setDatasources(response.datasources || []);

      // If we're editing a datasource, load its data
      const editingDatasource = new URLSearchParams(window.location.search).get(
        "edit"
      );
      if (editingDatasource) {
        const datasource = response.datasources?.find(
          (d) => d.name === editingDatasource
        );
        if (datasource) {
          setNewDatasource({
            name: datasource.name,
            type: datasource.type,
            authMethod: datasource.authMethod,
            httpMethod: datasource.httpMethod || "",
            testUrl: datasource.apiUrl || "",
            apiKey: datasource.apiKey?.key || "",
            apiKeyHeader: datasource.apiKey?.header || "X-API-Key",
            oauthClientId: datasource.oauth2?.clientId || "",
            oauthClientSecret: datasource.oauth2?.clientSecret || "",
            oauthTokenUrl: datasource.oauth2?.tokenUrl || "",
            oauthScope: datasource.oauth2?.scope || "",
            basicAuthUsername: datasource.basicAuth?.username || "",
            basicAuthPassword: datasource.basicAuth?.password || "",
            bearerToken: datasource.bearerToken || "",
            refreshToken: datasource.oauth2?.refreshToken || "",
            customHeaders: datasource.customHeaders || [],
            apiDataFormat: datasource.apiDataFormat || "json",
            apiRecordPath: datasource.apiRecordPath || "",
            requestBody: datasource.requestBody || "",
            soapAction: datasource.soapAction || "",
            soapEnvelope: datasource.soapEnvelope || "",
          });
        }
      }
    } catch (error) {
      console.error("Failed to load datasources:", error);
      setSnackDetails({
        open: true,
        message: <>Failed to load datasources</>,
        severity: "error",
      });
    } finally {
      setFormDataLoading(false);
    }
  };

  const handleDatasourceSubmit = async () => {
    // Check if we're in edit mode
    const editingDatasource = new URLSearchParams(window.location.search).get(
      "edit"
    );
    const isEditing = !!editingDatasource;
    if (
      !newDatasource.name ||
      !newDatasource.type ||
      !newDatasource.authMethod
    ) {
      setSnackDetails({
        open: true,
        message: <>Name, Type, and Auth Method are required</>,
        severity: "error",
      });
      return;
    }

    // For Zoho CRM, require a refresh token
    if (
      newDatasource.authMethod === "Zoho CRM" &&
      !newDatasource.refreshToken
    ) {
      setSnackDetails({
        open: true,
        message: (
          <>Please test the connection first to generate a refresh token</>
        ),
        severity: "error",
      });
      return;
    }

    try {
      setFormDataLoading(true);
      const datasourceData = {
        orgId: getStorageItem("organization-id", true, sessionStorage),
        name: newDatasource.name,
        type: newDatasource.type,
        authMethod: newDatasource.authMethod,
        httpMethod: newDatasource.httpMethod,
        customHeaders: newDatasource.customHeaders,
        apiUrl: newDatasource.testUrl,
        bearerToken: newDatasource.bearerToken,
        apiDataFormat: newDatasource.apiDataFormat,
        apiRecordPath: newDatasource.apiRecordPath,
        requestBody: newDatasource.requestBody,
        soapAction: newDatasource.soapAction,
        soapEnvelope: newDatasource.soapEnvelope,
        basicAuth: {
          username: newDatasource.basicAuthUsername,
          password: newDatasource.basicAuthPassword,
        },
        apiKey: {
          key: newDatasource.apiKey,
          header: newDatasource.apiKeyHeader,
        },
        oauth2:
            {
                oauthClientId: newDatasource.oauthClientId,
                oauthClientSecret: newDatasource.oauthClientSecret,
                oauthTokenUrl: newDatasource.oauthTokenUrl,
                oauthScope:
                  newDatasource.type !== "Zoho CRM"
                    ? newDatasource.oauthScope
                    : "",

                refreshToken: newDatasource.refreshToken || "",
            },
      };

      console.log("Saving datasource with data:", datasourceData);
      if (isEditing) {
        // If editing, use the original name as the key
        await saveDataSource({
          ...datasourceData,
          originalName: editingDatasource,
        });
      } else {
        // If creating new, just save normally
        await saveDataSource(datasourceData);
      }

      // After saving, reload datasources and clear edit mode
      await loadDatasources();

      setNewDatasource({
        name: "",
        type: "",
        authMethod: "",
        httpMethod: "",
        testUrl: "",
        apiKey: "",
        apiKeyHeader: "X-API-Key",
        oauthClientId: "",
        oauthClientSecret: "",
        oauthTokenUrl: "",
        oauthScope: "",
        basicAuthUsername: "",
        basicAuthPassword: "",
        bearerToken: "",
        refreshToken: "",
        customHeaders: [],
        apiDataFormat: "json",
        apiRecordPath: "",
        requestBody: "",
        soapAction: "",
        soapEnvelope: "",
      });
      setTestResponse(null);

      setSnackDetails({
        open: true,
        message: (
          <>
            {isEditing
              ? "Datasource updated successfully"
              : "Datasource added successfully"}
          </>
        ),
        severity: "success",
      });
    } catch (error) {
      setSnackDetails({
        open: true,
        message: <>Failed to save datasource</>,
        severity: "error",
      });
    } finally {
      setFormDataLoading(false);
    }
  };

  const handleTestDatasource = async () => {
    if (!newDatasource.testUrl) {
      setSnackDetails({
        open: true,
        message: <>Test URL is required</>,
        severity: "error",
      });
      return;
    }

    try {
      setFormDataLoading(true);
      const testConfig = {
        apiUrl: newDatasource.testUrl,
        authMethod: newDatasource.authMethod,
        httpMethod: newDatasource.httpMethod,
        apiKey: newDatasource.apiKey,
        apiKeyHeader: newDatasource.apiKeyHeader,
        oauthClientId: newDatasource.oauthClientId,
        oauthClientSecret: newDatasource.oauthClientSecret,
        oauthTokenUrl: newDatasource.oauthTokenUrl ||
          (newDatasource.type === "Zoho CRM"
            ? "https://accounts.zoho.com/oauth/v2/token"
            : ""),
        oauthScope:
          newDatasource.type !== "Zoho CRM" &&
          newDatasource.type !== "Genesys Cloud"
            ? newDatasource.oauthScope
            : "",
        basicAuthUsername: newDatasource.basicAuthUsername,
        basicAuthPassword: newDatasource.basicAuthPassword,
        bearerToken: newDatasource.bearerToken,
        customHeaders: newDatasource.customHeaders,
        datasourceType: newDatasource.type,

        refreshToken:
          newDatasource.authMethod === "Zoho CRM"
            ? newDatasource.refreshToken || ""
            : "",
        grantType:
          newDatasource.authMethod === "Zoho CRM" ? "refresh_token" : "",
        orgId: getStorageItem("organization-id", true, sessionStorage),
        requestBody: newDatasource.requestBody,
        soapAction: newDatasource.soapAction,
        soapEnvelope: newDatasource.soapEnvelope,
      };

      const response = await testAPIConnection(testConfig);
      setTestResponse(response);

      // For Zoho CRM, we're already using the refresh token directly
      // No need to save a new refresh token from the response
      setSnackDetails({
        open: true,
        message: <>Datasource connection test successful!</>,
        severity: "success",
      });
    } catch (error: any) {
      setTestResponse({
        error: true,
        message: error?.response?.data?.message || error?.message || "Connection test failed",
        details: error?.response?.data || error,
        status: error?.response?.status || "Unknown"
      });
      setSnackDetails({
        open: true,
        message: <>Datasource connection test failed</>,
        severity: "error",
      });
    } finally {
      setFormDataLoading(false);
    }
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }} className="jobs-view">
      <div className="m-2 text">Datasource Management</div>
      <Paper
        elevation={2}
        sx={{
          margin: "10px 5px 5px 5px",
          padding: "10px",
          border: "1px solid #ccc",
        }}
      >
        <div className="row">
          <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
            <TextField
              label="Datasource Name *"
              value={newDatasource.name}
              onChange={(e) =>
                setNewDatasource({ ...newDatasource, name: e.target.value })
              }
              size="small"
              sx={{ width: "100%" }}
            />
          </div>
          <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
            <TextField
              select
              label="Type *"
              value={newDatasource.type}
              onChange={(e) => {
                const newType = e.target.value;
                const newTypeDataSource = datasourceTypes.find(source => source.type === newType);
                if(newTypeDataSource) {
                  setNewDatasource({ ...newDatasource, ...newTypeDataSource });
                }
              }}
              size="small"
              sx={{ width: "100%" }}
            >
              {datasourceTypes.map((source) => (
                <MenuItem key={source.type} value={source.type}>
                  {source.type}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-2">
            <TextField
              label="Test URL"
              value={newDatasource.testUrl || ""}
              onChange={(e) =>
                setNewDatasource({ ...newDatasource, testUrl: e.target.value })
              }
              size="small"
              placeholder="https://api.example.com/test"
              sx={{ width: "100%" }}
            />
          </div>
        
          
          
            <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
              <TextField
                select
                label="Auth Method *"
                value={newDatasource.authMethod}
                onChange={(e) =>
                  setNewDatasource({
                    ...newDatasource,
                    authMethod: e.target.value,
                  })
                }
                size="small"
                sx={{ width: "100%" }}
                disabled={newDatasource.type !== "Custom API" && newDatasource.type !== "SOAP Web Service"}
              >
                {authMethods
                  .filter((method) => method !== "Zoho CRM")
                  .map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
              </TextField>
            </div>
          
          <>
            <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
              <TextField
                select
                  label="HTTP Method *"
                  value={newDatasource.httpMethod}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      httpMethod: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={newDatasource.type !== "Custom API" && newDatasource.type !== "SOAP Web Service"}
                >
                  {httpMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <>
                <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                  <TextField
                    select
                      label="API Data Format"
                      value={newDatasource.apiDataFormat || "json"}
                      onChange={(e) =>
                        setNewDatasource({
                          ...newDatasource,
                          apiDataFormat: e.target.value,
                        })
                      }
                      size="small"
                      sx={{ width: "100%" }}
                      disabled={newDatasource.type !== "SOAP Web Service" && newDatasource.type !== "Custom API"}
                    >
                      <MenuItem value="json">JSON</MenuItem>
                      <MenuItem value="xml">XML</MenuItem>
                    </TextField>
                  </div>
                  <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                    <TextField
                      label="API Record Path"
                      value={newDatasource.apiRecordPath || ""}
                      onChange={(e) =>
                        setNewDatasource({
                          ...newDatasource,
                          apiRecordPath: e.target.value,
                        })
                      }
                      size="small"
                      placeholder="e.g., data.records"
                      sx={{ width: "100%" }}
                      disabled={newDatasource.type === "Zoho CRM"}
                    />
                  </div>
                </>
              </>
          {newDatasource.type === "SOAP Web Service" && (
            <>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="SOAP Action"
                  value={newDatasource.soapAction}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      soapAction: e.target.value,
                    })
                  }
                  size="small"
                  placeholder="http://example.com/GetData"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-2">
                <TextField
                  label="SOAP Envelope (XML)"
                  value={newDatasource.soapEnvelope}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      soapEnvelope: e.target.value,
                    })
                  }
                  multiline
                  rows={6}
                  size="small"
                  placeholder='<?xml version="1.0" encoding="utf-8"?>\n<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">\n  <soap:Body>\n    <GetData xmlns="http://example.com/">\n      <!-- Your parameters here -->\n    </GetData>\n  </soap:Body>\n</soap:Envelope>'
                  sx={{ width: "100%" }}
                />
              </div>
            </>
          )}
          {(newDatasource.httpMethod === "POST" || newDatasource.httpMethod === "PUT" || newDatasource.httpMethod === "PATCH") && newDatasource.type !== "SOAP Web Service" && (
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-2">
              <TextField
                label="Request Body (JSON)"
                value={newDatasource.requestBody}
                onChange={(e) =>
                  setNewDatasource({
                    ...newDatasource,
                    requestBody: e.target.value,
                  })
                }
                multiline
                rows={4}
                size="small"
                placeholder='{"key": "value"}'
                sx={{ width: "100%" }}
              />
            </div>
          )}

          {newDatasource.authMethod === "Custom Headers" && (
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-2">
              <div className="mb-2">
                <span className="fw-bold">Custom Headers</span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const updatedHeaders = [
                      ...newDatasource.customHeaders,
                      { key: "", value: "" },
                    ];
                    setNewDatasource({
                      ...newDatasource,
                      customHeaders: updatedHeaders,
                    });
                  }}
                  sx={{ ml: 2 }}
                >
                  Add Header
                </Button>
              </div>

              {newDatasource.customHeaders.map((header, index) => (
                <div className="row mb-2" key={index}>
                  <div className="col-6">
                    <TextField
                      label="Header Name"
                      value={header.key}
                      onChange={(e) => {
                        const updatedHeaders = [...newDatasource.customHeaders];
                        updatedHeaders[index].key = e.target.value;
                        setNewDatasource({
                          ...newDatasource,
                          customHeaders: updatedHeaders,
                        });
                      }}
                      size="small"
                      fullWidth
                    />
                  </div>
                  <div className="col-6">
                    <TextField
                      label="Value"
                      value={header.value}
                      onChange={(e) => {
                        const updatedHeaders = [...newDatasource.customHeaders];
                        updatedHeaders[index].value = e.target.value;
                        setNewDatasource({
                          ...newDatasource,
                          customHeaders: updatedHeaders,
                        });
                      }}
                      size="small"
                      fullWidth
                    />
                  </div>
                  <div className="col-1 d-flex align-items-center">
                    <Button
                      color="error"
                      size="small"
                      onClick={() => {
                        const updatedHeaders = [...newDatasource.customHeaders];
                        updatedHeaders.splice(index, 1);
                        setNewDatasource({
                          ...newDatasource,
                          customHeaders: updatedHeaders,
                        });
                      }}
                      sx={{ minWidth: "auto", p: 1 }}
                    >
                      <DeleteIcon />
                    </Button>
                  </div>
                </div>
              ))}

              {newDatasource.customHeaders.length === 0 && (
                <div className="alert alert-info">
                  No custom headers added yet. Click "Add Header" to add
                  key-value pairs.
                </div>
              )}
            </div>
          )}
          {newDatasource.authMethod === "API Key" && (
            <>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="API Key *"
                  type="password"
                  value={newDatasource.apiKey}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      apiKey: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Header Name"
                  value={newDatasource.apiKeyHeader}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      apiKeyHeader: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
            </>
          )}
          {newDatasource.authMethod === "Bearer Token" && (
            <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
              <TextField
                label="Bearer Token *"
                type="password"
                value={newDatasource.bearerToken}
                onChange={(e) =>
                  setNewDatasource({
                    ...newDatasource,
                    bearerToken: e.target.value,
                  })
                }
                size="small"
                sx={{ width: "100%" }}
              />
            </div>
          )}
          {newDatasource.authMethod === "OAuth 2.0" && (
            <>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Client ID *"
                  value={newDatasource.oauthClientId}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthClientId: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Client Secret *"
                  type="password"
                  value={newDatasource.oauthClientSecret}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthClientSecret: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Token URL *"
                  value={newDatasource.oauthTokenUrl}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthTokenUrl: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>           
                <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                  <TextField
                    label="Scope"
                    value={newDatasource.oauthScope || ""}
                    onChange={(e) =>
                      setNewDatasource({
                        ...newDatasource,
                        oauthScope: e.target.value,
                      } as any)
                    }
                    size="small"
                    sx={{ width: "100%" }}
                    disabled={newDatasource.type !== "Microsoft Dynamics" && newDatasource.type !== "Custom API"}
                  />
                </div>
              
              
            </>
          )}
          {newDatasource.authMethod === "Zoho CRM" && (
            <>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Client ID *"
                  value={newDatasource.oauthClientId}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthClientId: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Client Secret *"
                  type="password"
                  value={newDatasource.oauthClientSecret}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthClientSecret: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Token URL *"
                  value={newDatasource.oauthTokenUrl}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      oauthTokenUrl: e.target.value,
                    })
                  }
                  size="small"
                  placeholder="https://accounts.zoho.com/oauth/v2/token"
                  sx={{ width: "100%" }}
                />
              </div>

              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Refresh Token *"
                  value={newDatasource.refreshToken || ""}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      refreshToken: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>

              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-2">
                <div className="alert alert-info">
                  <strong>Zoho CRM Setup:</strong>
                  <ol>
                    <li>Create a Self-Client in Zoho API Console</li>
                    <li>
                      Generate a refresh token using the Zoho API Console or the
                      authorization flow
                    </li>
                    <li>Enter the refresh token in the field above</li>
                    <li>
                      Use Token URL: https://accounts.zoho.com/oauth/v2/token
                    </li>
                    <li>
                      For testing, use the Test URL format:
                      https://www.zohoapis.com/crm/v2/contacts
                    </li>
                  </ol>
                </div>
              </div>
            </>
          )}
          {newDatasource.authMethod === "Basic Auth" && (
            <>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Username *"
                  value={newDatasource.basicAuthUsername}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      basicAuthUsername: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-6 mt-2">
                <TextField
                  label="Password *"
                  type="password"
                  value={newDatasource.basicAuthPassword}
                  onChange={(e) =>
                    setNewDatasource({
                      ...newDatasource,
                      basicAuthPassword: e.target.value,
                    })
                  }
                  size="small"
                  sx={{ width: "100%" }}
                />
              </div>
            </>
          )}
          <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-3 d-flex justify-content-end">
            <Button
              variant="outlined"
              onClick={handleTestDatasource}
              sx={{ mr: 2 }}
            >
              Test Connection
            </Button>
            {new URLSearchParams(window.location.search).get("edit") && (
              <Button
                variant="outlined"
                onClick={() => {
                  // Clear form and remove edit parameter
                  setNewDatasource({
                    name: "",
                    type: "",
                    authMethod: "",
                    httpMethod: "",
                    testUrl: "",
                    apiKey: "",
                    apiKeyHeader: "X-API-Key",
                    oauthClientId: "",
                    oauthClientSecret: "",
                    oauthTokenUrl: "",
                    oauthScope: "",
                    basicAuthUsername: "",
                    basicAuthPassword: "",
                    bearerToken: "",
                    refreshToken: "",
                    customHeaders: [],
                    apiDataFormat: "json",
                    apiRecordPath: "",
                    requestBody: "",
                    soapAction: "",
                    soapEnvelope: "",
                  });
                  setTestResponse(null);
                  const url = new URL(window.location.href);
                  url.searchParams.delete("edit");
                  window.history.pushState({}, "", url.toString());
                }}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleDatasourceSubmit}
              sx={{ color: "white", backgroundColor: "#1b2c48" }}
            >
              {new URLSearchParams(window.location.search).get("edit")
                ? "Update Datasource"
                : "Add Datasource"}
            </Button>
          </div>
          {testResponse && (
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 mt-3">
              <TextField
                label="Test Response"
                value={JSON.stringify(testResponse, null, 2)}
                multiline
                rows={10}
                size="small"
                InputProps={{
                  readOnly: true,
                }}
                sx={{ width: "100%" }}
              />
            </div>
          )}
        </div>
        <div className="row mt-3">
          <DataGrid
            rows={datasources}
            getRowId={(row) => row.name}
            columns={[
              { field: "name", headerName: "Name", flex: 1 },
              { field: "type", headerName: "Type", flex: 1 },
              { field: "authMethod", headerName: "Auth Method", flex: 1 },
              {
                field: "actions",
                type: "actions",
                headerName: "Actions",
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={() => {
                      console.log("Edit clicked for row:", row);
                      // Load the datasource data into the form
                      const editData = {
                        name: row.name,
                        type: row.type,
                        authMethod: row.authMethod,
                        httpMethod: row.httpMethod || "",
                        testUrl: row.apiUrl || "",
                        apiKey: row.apiKey?.key || "",
                        apiKeyHeader: row.apiKey?.header || "X-API-Key",
                        oauthClientId: row.oauth2?.clientId || "",
                        oauthClientSecret: row.oauth2?.clientSecret || "",
                        oauthTokenUrl: row.oauth2?.tokenUrl || "",
                        oauthScope: row.oauth2?.scope || "",
                        basicAuthUsername: row.basicAuth?.username || "",
                        basicAuthPassword: row.basicAuth?.password || "",
                        bearerToken: row.bearerToken || "",
                        refreshToken: row.oauth2?.refreshToken || "",
                        customHeaders: row.customHeaders || [],
                        apiDataFormat: row.apiDataFormat || "json",
                        apiRecordPath: row.apiRecordPath || "",
                        requestBody: row.requestBody || "",
                        soapAction: row.soapAction || "",
                        soapEnvelope: row.soapEnvelope || "",
                      };
                      console.log("Setting edit data:", editData);
                      setNewDatasource(editData);
                      // Add edit parameter to URL without page reload
                      const url = new URL(window.location.href);
                      url.searchParams.set("edit", row.name);
                      window.history.pushState({}, "", url.toString());
                    }}
                  />,
                  <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={async () => {
                      try {
                        setFormDataLoading(true);
                        await deleteDataSource(row.name);
                        await loadDatasources();
                        setSnackDetails({
                          open: true,
                          message: <>Datasource deleted successfully</>,
                          severity: "success",
                        });
                      } catch (error) {
                        setSnackDetails({
                          open: true,
                          message: <>Failed to delete datasource</>,
                          severity: "error",
                        });
                      } finally {
                        setFormDataLoading(false);
                      }
                    }}
                  />,
                ],
              },
            ]}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            pageSizeOptions={[5, 10]}
          />
        </div>
      </Paper>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isFormDataLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {getSnackDetails.open && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={getSnackDetails.open}
          autoHideDuration={5000}
          onClose={(_event, reason?: string) => {
            if (reason === "clickaway") {
              return;
            }
            setSnackDetails({ open: false, message: <></>, severity: "error" });
          }}
        >
          <Alert
            severity={getSnackDetails.severity as AlertColor}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {getSnackDetails.message as ReactNode}
          </Alert>
        </Snackbar>
      )}
    </Paper>
  );
};

export default Datasource;
